/**
 * 三端（web / webview / workshop）与 @repo/ui 共用的 React Query key 工厂。
 *
 * 约定：
 * - 每个域一个对象，`all` 是前缀，写操作后 `invalidateQueries({ queryKey: xxxKeys.all })` 可整域失效。
 * - 带参数的 key 把参数对象原样放进数组（结构化比较），切 Tab / 翻页 / 搜索各自独立缓存。
 * - 同一资源在不同页面 / 组件里必须用同一个 key，才能靠缓存去重（如用户资料、云空间配额）。
 * - 仅某一端专属的资源（如 workshop 的订单、橱窗）留在应用内的 `service/query-keys.ts`。
 */

export const userKeys = {
  all: ["user"] as const,
  /** 当前登录用户 */
  me: () => [...userKeys.all, "me"] as const,
  /** 任意用户的公开资料，列表每行头像 / 详情作者 / 可信查询共用 */
  profile: (uid: string) => [...userKeys.all, "profile", uid] as const,
  notificationSettings: () => [...userKeys.all, "notification-settings"] as const,
  privacySettings: () => [...userKeys.all, "privacy-settings"] as const,
  /** 设置中心「屏蔽用户」列表（UserAPI.getBlacklist），与模板保护的黑名单是两套接口 */
  blockedUsers: () => [...userKeys.all, "blocked-users"] as const,
  /** 设置中心「登录设备」列表（AuthAPI.getLoginDevices） */
  loginDevices: () => [...userKeys.all, "login-devices"] as const,
  /** 可信查询：某用户作为买 / 卖方的已完成委托数 */
  completedOrderCount: (uid: string) => [...userKeys.all, "completed-order-count", uid] as const,
};

export type TemplateListType = "received" | "uploaded" | "pending";

export const templateKeys = {
  all: ["templates"] as const,
  /**
   * 模板列表三 Tab；`search` 为空字符串时也要进 key，保证搜索前后缓存隔离。
   * web 是页码分页（带 `page`），webview 是无限滚动（不带 `page`，页码由 useInfiniteQuery 管）。
   */
  list: (params: { type: TemplateListType; search: string; pageSize: number; page?: number }) =>
    [...templateKeys.all, "list", params] as const,
  /** 详情；带 sendId 时是「某次发货视角」的详情，与不带 sendId 的缓存分开 */
  detail: (templateId: string, sendId?: string) =>
    [...templateKeys.all, "detail", templateId, sendId ?? ""] as const,
  /** 某模板的发货记录（分页） */
  sendRecords: (templateId: string, params: { page: number; pageSize: number }) =>
    [...templateKeys.all, "send-records", templateId, params] as const,
  /** 待签收数量角标 */
  pendingCount: () => [...templateKeys.all, "pending-count"] as const,
  /** 某用户的历史发货 / 收货统计 */
  useStatistics: (uid: string) => [...templateKeys.all, "use-statistics", uid] as const,
  /** 收发历史（无限滚动）；筛选条件（类型 / 对方 uid / 名称 / 接收方）都进 key */
  sendReceiveHistory: (params: Record<string, unknown>) =>
    [...templateKeys.all, "send-receive-history", params] as const,
  /** 模板溯源：解码配额 */
  decodeQuota: () => [...templateKeys.all, "decode-quota"] as const,
  /** 模板溯源：解码历史（分页） */
  decodeHistory: (params: Record<string, unknown>) =>
    [...templateKeys.all, "decode-history", params] as const,
};

export const storageKeys = {
  all: ["storage"] as const,
  /** 云空间配额，MinePanel / 设置页 / 详情 / 上传多处共用 */
  quota: () => [...storageKeys.all, "quota"] as const,
  files: (params: Record<string, unknown>) => [...storageKeys.all, "files", params] as const,
  /** 可购买的套餐列表 */
  packages: () => [...storageKeys.all, "packages"] as const,
};

export const blacklistKeys = {
  all: ["blacklist"] as const,
  /** 我的黑名单；web 页码分页带 `page`，webview 无限滚动不带 */
  mine: (params: { page?: number; pageSize: number }) =>
    [...blacklistKeys.all, "mine", params] as const,
  /** 我的黑名单公开设置 */
  mySettings: () => [...blacklistKeys.all, "my-settings"] as const,
  /** 我订阅的黑名单集合 */
  subscriptions: () => [...blacklistKeys.all, "subscriptions"] as const,
  /** 公共黑名单集合列表；web 页码分页带 `page`，webview 无限滚动不带 */
  shared: (params: { page?: number; pageSize: number; ownerUid?: string }) =>
    [...blacklistKeys.all, "shared", params] as const,
  /** 单个公共黑名单集合的元信息 */
  sharedSet: (setId: number) => [...blacklistKeys.all, "shared-set", setId] as const,
  /** 单个公共黑名单集合内的用户（分页 / 筛选） */
  sharedSetUsers: (setId: number, params: Record<string, unknown>) =>
    [...blacklistKeys.all, "shared-set-users", setId, params] as const,
  /** 某 UID 的黑名单命中检查 */
  check: (uid: string) => [...blacklistKeys.all, "check", uid] as const,
};

export const reportKeys = {
  all: ["report"] as const,
  detail: (reportId: string) => [...reportKeys.all, "detail", reportId] as const,
  /** 举报目标为橱窗时的橱窗信息 */
  stallInfo: (stallId: string) => [...reportKeys.all, "stall-info", stallId] as const,
};

export const aigcKeys = {
  all: ["aigc"] as const,
  quota: () => [...aigcKeys.all, "quota"] as const,
};
